'use client';

import { type FC, useMemo } from 'react';

import { GamesFilterSortDirection, GamesFilterSortType } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { getProviderIcon } from '@/helpers/cdn';
import type { CurrencyCode } from '@/helpers/currency';
import { oneRowSliderParams } from '@/helpers/games';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { useGameProviders } from '@/hooks/game';
import { useNavigation } from '@/hooks/navigation';
import { useGamesInfinite } from '@/modules/games/hooks';
import LobbyGamesSlider from '@/modules/lobby/components/LobbyGamesSlider';
import { useGetCommonStore } from '@/store/commonStore';
import { ScContainer } from '@/styles/common';
import {
  ScProviderHeader,
  ScProviderLogo,
  ScProviderName,
  ScProviderSection,
} from './styled';

interface IProviderGamesSectionProps {
  providerId: string;
}

export const ProviderGamesSection: FC<IProviderGamesSectionProps> = ({
  providerId,
}) => {
  const { generateLink } = useNavigation();

  const useCommonStore = useGetCommonStore();
  const wallets = useCommonStore.use.wallets();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const currencies = useMemo(
    () => wallets.map(({ currency }) => currency) as CurrencyCode[],
    [wallets]
  );

  const { games, isLoading } = useGamesInfinite({
    provider: providerId,
    device: isMobileDevice ? 'mobile' : 'desktop',
    currencies: currencies.length > 0 ? currencies : [DEMO_CURRENCY],
    sort: {
      type: GamesFilterSortType.POPULARITY,
      direction: GamesFilterSortDirection.ASC,
    },
  });

  const { currentProvider } = useGameProviders({ providerId });

  const providerLogoSrc = getProviderIcon(providerId, 'color', 'small');
  const providerTitle = currentProvider?.title ?? providerId;
  const providerAllLink = generateLink(`/games/all/${providerId}`);

  if (isLoading)
    return (
      <ScContainer>
        <ScProviderSection>
          <GamesSkeleton rows={1} withTitle />
        </ScProviderSection>
      </ScContainer>
    );

  if (!games.length) return null;

  return (
    <ScContainer>
      <ScProviderSection>
        <LobbyGamesSlider
          categoryId={providerId}
          gamesList={games}
          hideGamesCount
          seeAllLink={providerAllLink}
          sliderParams={oneRowSliderParams}
          title={
            <ScProviderHeader>
              <ScProviderName>
                {providerTitle}
                <ScProviderLogo>
                  <ImageComponent
                    alt={providerTitle}
                    className="img-provider"
                    height={24}
                    src={providerLogoSrc}
                    width={24}
                  />
                </ScProviderLogo>
              </ScProviderName>
            </ScProviderHeader>
          }
        />
      </ScProviderSection>
    </ScContainer>
  );
};
